import { FormControl, FormGroup, FormLabel } from '@mui/material'
import { Field } from 'formik'
import { TextField } from 'formik-mui'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { validateFieldWithYup } from '../validateFieldWithYup'
import { CountryPickerField } from './CountryPickerField'
import { StatePickerField } from './StatePickerField'

type AddressFieldProps = {
  name?: string
  required?: boolean
}

export const AddressField = ({ name = 'address', required }: AddressFieldProps) => {
  const { t } = useTranslation('@cibo/ui/AddressField')

  const validationSchema = useMemo(
    () =>
      Yup.object({
        street: Yup.string().required(t('required')).max(255, t('max')),
        city: Yup.string().required(t('required')).max(255, t('max')),
        postalCode: Yup.string().required(t('required')).max(255, t('max')).nullable(),
        state: Yup.object().nullable(),
        country: Yup.object().required(t('required')),
      }),
    []
  )

  return (
    <Field
      name={name}
      id={name}
      validate={required ? validateFieldWithYup(validationSchema) : undefined}
    >
      {() => (
        <FormGroup>
          <FormControl>
            <FormLabel htmlFor={`${name}.street`} required={required}>
              {t('street')}
            </FormLabel>
            <Field id={`${name}.street`} name={`${name}.street`} component={TextField} />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor={`${name}.street_2`}>{t('street_2')}</FormLabel>
            <Field id={`${name}.street_2`} name={`${name}.street_2`} component={TextField} />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor={`${name}.city`} required={required}>
              {t('city')}
            </FormLabel>
            <Field id={`${name}.city`} name={`${name}.city`} component={TextField} />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor={`${name}.postalCode`} required={required}>
              {t('postalCode')}
            </FormLabel>
            <Field id={`${name}.postalCode`} name={`${name}.postalCode`} component={TextField} />
          </FormControl>

          <FormControl disabled>
            <FormLabel htmlFor={`${name}.state`}>{t('state')}</FormLabel>
            <StatePickerField name={`${name}.state`} />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor={`${name}.country`} required={required}>
              {t('country')}
            </FormLabel>
            <CountryPickerField name={`${name}.country`} />
          </FormControl>
        </FormGroup>
      )}
    </Field>
  )
}
